import * as React from "react";
import { createContext, useContext, useState } from "react";

export interface UiAuthUser {
  name: string;
  email: string;
  role: string;
  apiKeyAnonymized: string;
}

interface UiAuthContextData {
  token: string | null;
  user: UiAuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (token: string, user: UiAuthUser) => void;
  logout: () => void;
}

const UiAuthContext = createContext<UiAuthContextData>({} as UiAuthContextData);

export function UiAuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("ui_token"));
  const [user, setUser] = useState<UiAuthUser | null>(() => {
    const storedUser = localStorage.getItem("ui_user");
    return storedUser ? (JSON.parse(storedUser) as UiAuthUser) : null;
  });

  const login = (newToken: string, newUser: UiAuthUser): void => {
    localStorage.setItem("ui_token", newToken);
    localStorage.setItem("ui_user", JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  const logout = (): void => {
    localStorage.removeItem("ui_token");
    localStorage.removeItem("ui_user");
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = Boolean(token && user);
  const isAdmin = Boolean(user && user.role.toUpperCase() === "ADMIN");

  return (
    <UiAuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated,
        isAdmin,
        login,
        logout,
      }}
    >
      {children}
    </UiAuthContext.Provider>
  );
};

export const useUiAuth = (): UiAuthContextData => {
  return useContext(UiAuthContext);
};
